import { UserResponse } from '../../../providers/User/User.model';
import { BaseQueryParams } from '../../Global.model';
import { AvailabilityStatus } from '../availability/availability.model';
import { CarrierResponse } from '../carriers/carriers.model';
import { DriverFile } from '../driverFile/driverFile.model';
import { DriverProfile } from '../driverProfile/driverProfile.model';
import { EldConnectionInfo, EldDriver, HoursOfService } from '../eld/eld.model';
import { Equipment } from '../equipment/equipment.model';
import { ISettlementDriverLoadAdjustmentType } from '../settlement/settlement.model';
import { TRIP_DRIVER_PAY_UNIT } from '../trips/trips.model';

export enum DRIVER_TYPE {
  CONTRACTOR = 'CONTRACTOR',
  EMPLOYEE = 'EMPLOYEE'
}

export enum DRIVER_DEFAULT_PAY_RATE_TYPE {
  PERCENTAGE = 'PERCENTAGE',
  PER_HOUR = 'PER_HOUR',
  PER_MILE = 'PER_MILE',
  FLAT = 'FLAT'
}

export interface AvailabilityStatusData {
  status: AvailabilityStatus;
  unavailable_start_date?: string;
  unavailable_end_date?: string;
}

/**
 * Driver information
 */
export interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  initials: string;
  email_address?: string;
  phone?: string;
  company_name?: string;
  loads_completed?: string;
  deleted_at?: null;
  tenant_id?: string;
  default_truck_id?: string;
  default_truck?: Equipment;
  default_trailer?: Equipment;
  default_trailer_id?: string;
  default_pay_rate_type?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  load_driver_pay_other_payment?: ISettlementDriverLoadAdjustmentType[];
  default_load_driver_other_pay?: ISettlementDriverLoadAdjustmentType[];
  readable_pay_rate_type?: string;
  pay_rate?: string;
  formatted_pay_rate: string;
  type?: DRIVER_TYPE;
  carrier_id?: string;
  carrier?: CarrierResponse;
  twuser_id?: string;
  twuser?: TwUserDriver;
  deduct_fuel_purchase_on_settlement?: boolean;
  paid_ytd?: number;
  notes?: string;
  availability_status: AvailabilityStatusData;
  driver_profile: DriverProfile | null;
  driver_files: DriverFile[] | null;
  eld_connection?: EldConnectionInfo;
  eld_connection_id?: string;
  eld_data?: EldDriver;
  hours_of_service?: HoursOfService;
  // TODO bring back when availability is refactored
  // availability_status: AvailabilityStatus | null;
}

/**
 * Driver creation data for request
 */
export interface CreateDriverParams {
  first_name: string;
  last_name: string;
  email_address?: string;
  phone?: string | null;
  company_name?: string;
  default_truck_id?: string;
  default_trailer_id?: string;
  default_pay_rate_type?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  pay_rate?: string;
  type?: DRIVER_TYPE;
  carrier_id?: string;
  create_user_and_send_invite?: boolean;
  deduct_fuel_purchase_on_settlement?: boolean;
  eld_connection_id?: string | null;
  eld_data?: EldDriver | null;
}

/**
 * Driver data required from user in creation
 */
export interface CreateDriverData {
  firstName: string;
  lastName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  defaultTruckId?: string;
  defaultTrailerId?: string;
  defaultPayRateType?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT;
  payRate?: string;
  type?: DRIVER_TYPE;
  carrierId?: string;
  createUserAndSendInvite?: boolean;
  deductFuelPurchaseOnSettlement?: boolean;
  notes?: string;
  eldConnectionId?: string;
  eldData?: EldDriver;
}

/**
 * Driver data required from user in contact details
 */
export type DriverContactDetails = Pick<CreateDriverData, 'firstName' | 'lastName'> &
  Partial<Pick<CreateDriverData, 'email' | 'phone'>>;

/**
 * Driver data required from user in payment info
 */
export type DriverPaymentInfo = Partial<
  Pick<
    CreateDriverData,
    | 'type'
    | 'carrierId'
    | 'companyName'
    | 'defaultPayRateType'
    | 'payRate'
    | 'deductFuelPurchaseOnSettlement'
  >
>;

export type DriverOperations = Partial<
  Pick<
    CreateDriverData,
    'defaultTruckId' | 'defaultTrailerId' | 'eldConnectionId' | 'eldData'
    // TODO: | 'dispatchGroupId'
    // TODO: | 'tracking'
  >
>;

export type DriverNotes = Partial<Pick<CreateDriverData, 'notes'>>;

export type DriverDialogWarningData = Pick<CreateDriverData, 'firstName' | 'lastName'>;

export type TwUserDriver = Pick<UserResponse, 'status'>;

export interface FilterParams {
  driverId?: string;
  carrierId?: string;
  driverFileStatus?: string[];
}

export interface SearchDriverParams {
  filters?: FilterParams;
  params?: BaseQueryParams;
}
